import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

const STORAGE_KEY = "dailyfi:ui:sensitive-values-hidden";

type SensitiveValuesContextValue = {
  valuesHidden: boolean;
  showValues: boolean;
  setValuesHidden: (hidden: boolean) => void;
  toggleValuesHidden: () => void;
};

const SensitiveValuesContext = createContext<SensitiveValuesContextValue>({
  valuesHidden: false,
  showValues: true,
  setValuesHidden: () => {},
  toggleValuesHidden: () => {},
});

export function SensitiveValuesProvider({ children }: { children: ReactNode }) {
  const [valuesHidden, setValuesHiddenState] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const storedValue = window.localStorage.getItem(STORAGE_KEY);
    if (storedValue === "1") {
      setValuesHiddenState(true);
    }
  }, []);

  const setValuesHidden = (hidden: boolean) => {
    setValuesHiddenState(hidden);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, hidden ? "1" : "0");
    }
  };

  const value = useMemo<SensitiveValuesContextValue>(
    () => ({
      valuesHidden,
      showValues: !valuesHidden,
      setValuesHidden,
      toggleValuesHidden: () => setValuesHidden(!valuesHidden),
    }),
    [valuesHidden],
  );

  return <SensitiveValuesContext.Provider value={value}>{children}</SensitiveValuesContext.Provider>;
}

export function useSensitiveValues() {
  return useContext(SensitiveValuesContext);
}
